import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { UserPlus, Pencil } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterUserDialog = ({ onSuccess, editUser = null, onClose }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'referent'
  });

  // Pré-remplir si édition
  useEffect(() => {
    if (editUser) {
      // Normaliser le rôle (convertir "alliance" en "animateur")
      let normalizedRole = editUser.role || 'referent';
      if (normalizedRole === 'alliance' || normalizedRole === 'Alliance') {
        normalizedRole = 'animateur';
      }
      
      setFormData({
        email: editUser.email || '',
        password: '', // Ne pas afficher le mot de passe
        nom: editUser.nom || '',
        prenom: editUser.prenom || '',
        role: normalizedRole
      });
      setOpen(true);
    }
  }, [editUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (editUser) {
        // Mode édition
        const updateData = {
          email: formData.email,
          nom: formData.nom,
          prenom: formData.prenom,
          role: formData.role
        };
        // Ajouter le mot de passe seulement si renseigné
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API_URL}/api/users/${editUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Utilisateur modifié avec succès');
      } else {
        // Mode création
        await axios.post(`${API_URL}/api/auth/register`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Utilisateur créé avec succès');
      }
      
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ email: '', password: '', nom: '', prenom: '', role: 'referent' });
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleClose(); else setOpen(true); }}>
      {!editUser && (
        <DialogTrigger asChild>
          <Button data-testid="add-user-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
            <UserPlus className="w-4 h-4 mr-2" />
            Nouvel utilisateur
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px] rounded-3xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>
            {editUser ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
          </DialogTitle>
          <DialogDescription>
            {editUser ? 'Modifiez les informations de l\'utilisateur' : 'Remplissez les informations du nouvel utilisateur'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom</Label>
              <Input
                id="nom"
                data-testid="user-nom-input"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom</Label>
              <Input
                id="prenom"
                data-testid="user-prenom-input"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="user-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Mot de passe {editUser && <span className="text-slate-400 text-xs">(laisser vide pour ne pas modifier)</span>}
            </Label>
            <Input
              id="password"
              data-testid="user-password-input"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editUser}
              className="rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Rôle</Label>
            <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
              <SelectTrigger data-testid="user-role-select" className="rounded-xl">
                <SelectValue placeholder="Sélectionner un rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrateur</SelectItem>
                <SelectItem value="referent">Référent de Parcours</SelectItem>
                <SelectItem value="animateur">Intervenant</SelectItem>
                <SelectItem value="financeur">Financeur</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="rounded-xl">
              Annuler
            </Button>
            <Button type="submit" data-testid="user-submit-button" disabled={loading} className="rounded-xl">
              {loading ? (editUser ? 'Modification...' : 'Création...') : (editUser ? 'Modifier' : 'Créer')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterUserDialog;